import React, { useEffect, useState, useRef } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, Sparkles, RefreshCw, Globe, Zap, AlertTriangle, CheckCircle2,
    TrendingUp, TrendingDown, ChevronRight, ExternalLink, ShieldCheck, Info,
    FileText, Search, Layout as LayoutIcon, MapPin, Rocket, Play,
    HelpCircle, Users, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GoogleConnections, { useGoogleOAuthReturn } from "@/components/GoogleConnections";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

const CATEGORY_META = {
    technical: { label: "Technical SEO", Icon: Zap, color: "cyan" },
    on_page: { label: "On-Page SEO", Icon: LayoutIcon, color: "indigo" },
    content: { label: "Content SEO", Icon: FileText, color: "emerald" },
    local: { label: "Local SEO", Icon: MapPin, color: "amber" },
    keywords: { label: "Keyword Opportunities", Icon: Search, color: "rose" },
    aeo: { label: "AEO", Icon: Sparkles, color: "indigo" },
    geo: { label: "GEO / AI Readiness", Icon: Globe, color: "cyan" },
};const SEVERITY_META = {
    critical: { color: "border-rose-500/40 bg-rose-500/15 text-rose-100", label: "Critical" },
    high: { color: "border-rose-500/30 bg-rose-500/10 text-rose-200", label: "High" },
    medium: { color: "border-amber-500/30 bg-amber-500/10 text-amber-200", label: "Medium" },
    low: { color: "border-slate-500/30 bg-slate-500/10 text-slate-300", label: "Low" },
};

export default function SEO() {
    const { currentBusiness } = useApp();
    const [scan, setScan] = useState(null);
    const [diff, setDiff] = useState({ has_previous: false });
    const [history, setHistory] = useState([]);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(null);
    const [tab, setTab] = useState("actions");
    const [google, setGoogle] = useState(null);
    const pollRef = useRef(null);

    const [fixDialog, setFixDialog] = useState(null); // {issue, fix, submitting}

    const load = async () => {
        if (!currentBusiness) return;
        try {
            const [r, hist, g] = await Promise.all([
                api.seoLatest(currentBusiness.id),
                api.seoScans(currentBusiness.id),
                api.googleStatus(currentBusiness.id),
            ]);
            setScan(r.scan);
            setDiff(r.diff || { has_previous: false });
            setHistory(hist.scans || []);
            setGoogle(g.google);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { load(); }, [currentBusiness?.id]); // eslint-disable-line
    // Returning from Google's consent screen (?code&state) finishes the connection.
    useGoogleOAuthReturn(currentBusiness?.id, load);

    const startScan = async (kind) => {
        if (!currentBusiness) return;
        try {
            const r = await api.seoStartScan(currentBusiness.id, { kind });
            setRunning(true);
            setProgress({ stage: "Starting…", progress: 5, scan_id: r.scan_id });
            pollScan(r.scan_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Scan failed to start.");
        }
    };
    const pollScan = (scanId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.seoGetScan(currentBusiness.id, scanId, false);
                setProgress({ stage: r.scan.stage, progress: r.scan.progress, scan_id: scanId });
                if (r.scan.status === "completed" || r.scan.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setRunning(false);
                    setProgress(null);
                    await load();
                    if (r.scan.status === "completed") {
                        toast.success("SEO scan complete.");
                    } else {
                        toast.error(`Scan failed: ${r.scan.error || "unknown"}`);
                    }
                }
            } catch { /* ignore */ }
        }, 2500);
    };
    useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

    const fixWithAI = async (issue) => {
        // Open dialog and fetch the AI proposed fix; user then approves or dismisses.
        setFixDialog({ issue, fix: null, submitting: true });
        try {
            const r = await api.seoFixWithAI(currentBusiness.id, {
                scan_id: scan.id,
                issue_id: issue.id,
                sample_url: (issue.affected_pages || [])[0],
            });
            setFixDialog({ issue, fix: r.fix, approval: r.approval, submitting: false });
        } catch {
            toast.error("AI fix failed.");
            setFixDialog(null);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto space-y-5 fade-in" data-testid="seo-page">
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="label-eyebrow text-cyan-300">SEO & Visibility</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                        Website & SEO Engine
                    </h1>
                    <p className="mt-2 text-sm text-slate-400 flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
                        Every result is grounded in real crawled data — never estimated.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => startScan("quick")} disabled={running}
                        data-testid="seo-quick-scan"
                        className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 gap-1.5">
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        Quick Scan
                    </Button>
                    <Button onClick={() => startScan("full")} disabled={running}
                        data-testid="seo-full-scan"
                        variant="outline"
                        className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5">
                        <Rocket className="h-4 w-4" /> Full Scan
                    </Button>
                    {scan && (
                        <Button onClick={() => startScan(scan.kind || "quick")} disabled={running}
                            data-testid="seo-reanalyse"
                            variant="outline"
                            className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5">
                            <RefreshCw className="h-4 w-4" /> Reanalyse
                        </Button>
                    )}
                </div>
            </div>

            {progress && (
                <div className="surface rounded-2xl p-4" data-testid="seo-progress">
                    <div className="flex items-center gap-3">
                        <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
                        <div className="flex-1">
                            <div className="text-sm text-slate-100">{progress.stage}</div>
                            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full bg-cyan-400 transition-all"
                                     style={{ width: `${progress.progress}%` }} />
                            </div>
                        </div>
                        <div className="text-xs mono text-slate-500">{progress.progress}%</div>
                    </div>
                </div>
            )}

            {google && <GoogleConnections google={google} businessId={currentBusiness?.id} onChange={load} />}

            {!scan ? (
                <div className="surface rounded-2xl p-12 text-center text-slate-400" data-testid="seo-empty">
                    <Globe className="h-8 w-8 text-slate-500 mx-auto mb-3" />
                    <p className="text-sm">Run a scan to see your Website & SEO report.</p>
                </div>
            ) : (
                <>
                    {/* Management summary */}
                    <div className="surface rounded-2xl p-5" data-testid="seo-summary">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="h-4 w-4 text-indigo-300" />
                            <div className="label-eyebrow text-indigo-300">Management summary · AI Analysis</div>
                        </div>
                        <p className="text-sm text-slate-200 leading-relaxed">{scan.summary}</p>
                    </div>

                    {/* Scores grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="seo-scores">
                        {Object.entries(scan.scores || {}).filter(([k]) => k !== "overall_seo").map(([k, v]) => (
                            <ScoreCard key={k} name={k} data={v} />
                        ))}
                    </div>

                    {/* Diff vs previous */}
                    {diff.has_previous && (
                        <div className="surface rounded-2xl p-4" data-testid="seo-diff">
                            <div className="label-eyebrow mb-2">Vs previous scan</div>
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                                {Object.entries(diff.score_delta || {}).map(([k, v]) => (
                                    <span key={k} data-testid={`seo-diff-${k}`}
                                          className={`flex items-center gap-1 ${
                                              v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-slate-400"
                                          }`}>
                                        {v > 0 ? <TrendingUp className="h-3 w-3" />
                                          : v < 0 ? <TrendingDown className="h-3 w-3" />
                                          : <Info className="h-3 w-3" />}
                                        {k}: {v > 0 ? "+" : ""}{v}
                                    </span>
                                ))}
                                <span className="text-slate-500">·</span>
                                <span className="text-emerald-300">Fixed: {diff.fixed?.length || 0}</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-rose-300">New: {diff.new_problems?.length || 0}</span>
                            </div>
                        </div>
                    )}

                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto">
                            <TabsTrigger value="actions" data-testid="seo-tab-actions">
                                <Rocket className="h-3.5 w-3.5 mr-1.5" /> Priority actions
                            </TabsTrigger>
                            {Object.entries(CATEGORY_META).map(([k, v]) => (
                                <TabsTrigger key={k} value={k} data-testid={`seo-tab-${k}`}>
                                    <v.Icon className="h-3.5 w-3.5 mr-1.5" /> {v.label}
                                </TabsTrigger>
                            ))}
                            <TabsTrigger value="questions" data-testid="seo-tab-questions">
                                <HelpCircle className="h-3.5 w-3.5 mr-1.5" /> Questions
                            </TabsTrigger>
                            <TabsTrigger value="visibility" data-testid="seo-tab-visibility">
                                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Visibility
                            </TabsTrigger>
                            <TabsTrigger value="competitors" data-testid="seo-tab-competitors">
                                <Users className="h-3.5 w-3.5 mr-1.5" /> Competitors
                            </TabsTrigger>
                            <TabsTrigger value="pages" data-testid="seo-tab-pages">
                                Crawled pages
                            </TabsTrigger>
                            <TabsTrigger value="psi" data-testid="seo-tab-psi">
                                PageSpeed
                            </TabsTrigger>
                            <TabsTrigger value="history" data-testid="seo-tab-history">
                                History
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="actions" className="mt-4">
                            <div className="space-y-3" data-testid="seo-priority-actions">
                                {(scan.priority_actions || []).map((a, i) => (
                                    <PriorityCard key={a.id || i} action={a} onFix={fixWithAI} />
                                ))}
                                {!scan.priority_actions?.length && (
                                    <div className="surface rounded-2xl p-8 text-center text-slate-500 text-sm">
                                        No priority actions.
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        {Object.keys(CATEGORY_META).map((cat) => (
                            <TabsContent key={cat} value={cat} className="mt-4">
                                {cat === "keywords" ? (
                                    <KeywordsPanel keywords={scan.keywords} issues={(scan.issues || []).filter((i) => i.category === "keywords")} onFix={fixWithAI} />
                                ) : (
                                    <IssuesList
                                        issues={(scan.issues || []).filter((i) => i.category === cat)}
                                        onFix={fixWithAI}
                                    />
                                )}
                            </TabsContent>
                        ))}

                        <TabsContent value="pages" className="mt-4">
                            <PagesTable pages={scan.pages || []} />
                        </TabsContent>

                        <TabsContent value="psi" className="mt-4">
                            <PSIPanel psi={scan.psi} />
                        </TabsContent>

                        <TabsContent value="history" className="mt-4">
                            <HistoryList history={history} />
                        </TabsContent>

                        <TabsContent value="questions" className="mt-4">
                            <QuestionsPanel scan={scan} businessId={currentBusiness?.id} />
                        </TabsContent>
                        <TabsContent value="visibility" className="mt-4">
                            <VisibilityPanel scan={scan} businessId={currentBusiness?.id} />
                        </TabsContent>
                        <TabsContent value="competitors" className="mt-4">
                            <CompetitorsPanel businessId={currentBusiness?.id} />
                        </TabsContent>
                    </Tabs>
                </>
            )}

            {fixDialog && (
                <FixWithAIDialog dialog={fixDialog} onClose={() => setFixDialog(null)} />
            )}
        </div>
    );
}

function FixWithAIDialog({ dialog, onClose }) {
    const { issue, fix, submitting } = dialog;
    const isSchema = issue.fix_kind === "schema";
    const proposedText = typeof fix?.proposed === "string" ? fix.proposed
        : (fix?.proposed ? JSON.stringify(fix.proposed, null, 2) : "");
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl bg-[#0B0F19] border-white/10 text-slate-100" data-testid="seo-fix-dialog">
                <DialogHeader>
                    <DialogTitle>Fix with AI — {issue.title}</DialogTitle>
                    <DialogDescription className="text-xs text-slate-400">
                        Review the current content and the AI-drafted replacement. Approval required — the fix is routed through the Approval Inbox before it's applied.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="border-cyan-500/20 bg-cyan-500/5 text-cyan-200">
                            source: {fix?.source || "AI_ANALYSIS"}
                        </Badge>
                        <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-200">
                            Approval required · seo_fix routed to Approval Inbox
                        </Badge>
                    </div>
                    {submitting ? (
                        <div className="py-12 text-center text-slate-400 text-sm">
                            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                            AI is drafting your fix…
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <div className="label-eyebrow mb-1">Current</div>
                                <pre data-testid="seo-fix-current" className={`text-xs p-3 rounded-xl bg-white/[0.03] border border-white/10 text-slate-300 whitespace-pre-wrap ${isSchema ? "font-mono" : ""}`}>
                                    {fix?.current || <span className="text-rose-300">(none)</span>}
                                </pre>
                            </div>
                            <div>
                                <div className="label-eyebrow mb-1 text-indigo-300">AI proposed</div>
                                <pre data-testid="seo-fix-proposed" className={`text-xs p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/20 text-slate-100 whitespace-pre-wrap ${isSchema ? "font-mono" : ""}`}>
                                    {proposedText || "—"}
                                </pre>
                                {fix?.notes && (
                                    <p className="mt-1.5 text-[11px] text-slate-500 italic">{fix.notes}</p>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5">
                        <div className="text-[11px] text-slate-500">
                            {fix ? "Approval created — review in the Approval Inbox to apply."
                                 : "Drafting via AI…"}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={onClose}
                                data-testid="seo-fix-close"
                                className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100">
                                Close
                            </Button>
                            {fix && proposedText && (
                                <Button onClick={() => {
                                    navigator.clipboard?.writeText(proposedText);
                                    toast.success("Copied to clipboard.");
                                }}
                                    data-testid="seo-fix-copy"
                                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 gap-1.5">
                                    <FileText className="h-4 w-4" /> Copy
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function QuestionsPanel({ scan, businessId }) {
    const questions = scan?.questions || [];
    const [busy, setBusy] = useState(null);
    const [answer, setAnswer] = useState(null);
    const generate = async (q) => {
        setBusy(q.id);
        try {
            const r = await api.aeoGenerateAnswer(businessId, { scan_id: scan.id, question_id: q.id });
            setAnswer({ question: q, ...r.answer });
            toast.success("AI answer drafted — review in Approvals.");
        } catch { toast.error("Generate failed."); }
        finally { setBusy(null); }
    };
    const statusColor = { strong: "text-emerald-300", partial: "text-amber-300", missing: "text-rose-300" };
    return (
        <div className="space-y-3" data-testid="questions-panel">
            <div className="text-xs text-slate-400 flex items-center gap-2">
                <Info className="h-3 w-3" /> Customer questions discovered via AI, coverage checked against real crawled content.
            </div>
            <ul className="space-y-2">
                {questions.map((q) => (
                    <li key={q.id} data-testid={`question-${q.id}`} className="surface rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-slate-100">{q.question}</span>
                                    <Badge variant="outline" className="border-white/10 text-slate-400 text-[10px]">{q.priority}</Badge>
                                    <Badge variant="outline" className="border-indigo-500/20 bg-indigo-500/5 text-indigo-200 text-[10px]">{q.source}</Badge>
                                    <Badge variant="outline" className={`text-[10px] border-white/10 ${statusColor[q.coverage?.status] || "text-slate-400"}`}
                                        data-testid={`question-coverage-${q.id}`}>
                                        Coverage: {q.coverage?.status || "unknown"}
                                    </Badge>
                                </div>
                                {q.coverage?.evidence_url && (
                                    <a href={q.coverage.evidence_url} target="_blank" rel="noreferrer"
                                        className="mt-1 text-[11px] text-cyan-300 hover:text-cyan-200 mono flex items-center gap-1">
                                        <ExternalLink className="h-2.5 w-2.5" /> {q.coverage.evidence_url}
                                    </a>
                                )}
                                {q.coverage?.evidence_text && (
                                    <p className="mt-1 text-[11px] text-slate-500 italic">{q.coverage.evidence_text}</p>
                                )}
                            </div>
                            {q.coverage?.status !== "strong" && (
                                <Button size="sm" onClick={() => generate(q)} disabled={busy === q.id}
                                    data-testid={`question-generate-${q.id}`}
                                    className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5 h-8">
                                    {busy === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                                    {q.coverage?.status === "partial" ? "Improve" : "Create"} answer
                                </Button>
                            )}
                        </div>
                    </li>
                ))}
                {!questions.length && <li className="text-sm text-slate-500 py-8 text-center">No questions yet.</li>}
            </ul>
            {answer && (
                <Dialog open onOpenChange={(o) => !o && setAnswer(null)}>
                    <DialogContent className="max-w-2xl bg-[#0B0F19] border-white/10 text-slate-100" data-testid="aeo-answer-dialog">
                        <DialogHeader>
                            <DialogTitle>AI Answer — {answer.question.question}</DialogTitle>
                            <DialogDescription className="text-xs text-slate-400">Approval required · aeo_fix routed to Approval Inbox.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 text-sm">
                            <div>
                                <div className="label-eyebrow mb-1 text-indigo-300">Suggested heading</div>
                                <div className="text-slate-100">{answer.suggested_heading}</div>
                            </div>
                            <div>
                                <div className="label-eyebrow mb-1 text-indigo-300">Proposed answer</div>
                                <p className="text-slate-200 whitespace-pre-wrap">{answer.answer}</p>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function VisibilityPanel({ scan, businessId }) {
    const [history, setHistory] = useState([]);
    const [queries, setQueries] = useState("");
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try { const r = await api.visibilityHistory(businessId); setHistory(r.tests || []); } catch {}
    };
    useEffect(() => { load(); }, [businessId]); // eslint-disable-line
    const run = async () => {
        const qs = queries.split("\n").map((s) => s.trim()).filter(Boolean);
        if (!qs.length) return;
        setBusy(true);
        try {
            await api.visibilityTest(businessId, { queries: qs });
            toast.success("AI visibility probes complete.");
            setQueries("");
            load();
        } catch { toast.error("Test failed."); }
        finally { setBusy(false); }
    };
    const initial = scan?.ai_visibility_probes || [];
    return (
        <div className="space-y-4" data-testid="visibility-panel">
            <div className="rounded-2xl bg-amber-500/[0.06] border border-amber-500/20 p-3 text-xs text-amber-200">
                <Info className="h-3 w-3 inline mr-1" /> {scan?.ai_visibility_disclaimer || "AI VISIBILITY TEST — directional snapshot, not a permanent ranking."}
            </div>
            <div className="surface rounded-2xl p-4">
                <div className="label-eyebrow mb-2">Run new visibility probes (one query per line)</div>
                <textarea data-testid="visibility-queries" value={queries} onChange={(e) => setQueries(e.target.value)} rows={3}
                    placeholder="Best dentist near me\nInvisalign providers in New York"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-2 text-sm text-slate-100" />
                <div className="mt-2 flex justify-end">
                    <Button onClick={run} disabled={busy || !queries.trim()} data-testid="visibility-run"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        Run visibility test
                    </Button>
                </div>
            </div>
            {initial.length > 0 && (
                <div className="surface rounded-2xl p-4">
                    <div className="label-eyebrow mb-2">From this scan</div>
                    <ProbeList probes={initial} />
                </div>
            )}
            <div className="surface rounded-2xl p-4">
                <div className="label-eyebrow mb-2">History (source: AI_VISIBILITY_TEST)</div>
                <ProbeList probes={history} />
            </div>
        </div>
    );
}

function ProbeList({ probes }) {
    if (!probes || probes.length === 0) return <div className="text-xs text-slate-500 text-center py-4">No probes yet.</div>;
    return (
        <ul className="space-y-2" data-testid="visibility-probes">
            {probes.slice(0, 30).map((p, i) => (
                <li key={p.id || i} className="p-3 rounded-xl bg-white/[0.02] border border-white/10">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                        <Badge variant="outline" className="border-white/10 text-slate-300">{p.engine_label || p.engine_key}</Badge>
                        <Badge variant="outline" className={p.business_mentioned
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                            : "border-rose-500/30 bg-rose-500/10 text-rose-200"}>
                            {p.business_mentioned ? "Business mentioned" : "Not mentioned"}
                        </Badge>
                        <span className="text-slate-500 mono text-[10px]">{p.checked_at?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                    <div className="mt-1.5 text-sm text-slate-100 font-semibold">{p.query}</div>
                    {p.response_text && <p className="mt-1 text-xs text-slate-400 line-clamp-3">{p.response_text}</p>}
                    {p.competitors_mentioned?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {p.competitors_mentioned.map((c) => (
                                <Badge key={c} variant="outline" className="border-amber-500/20 bg-amber-500/5 text-amber-200 text-[10px]">
                                    Competitor: {c}
                                </Badge>
                            ))}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

function CompetitorsPanel({ businessId }) {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ name: "", website: "" });
    const [busy, setBusy] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const load = async () => {
        try { const r = await api.competitorsList(businessId); setList(r.competitors || []); } catch {}
    };
    useEffect(() => { load(); }, [businessId]); // eslint-disable-line
    const add = async () => {
        if (!form.name.trim()) return;
        try {
            await api.competitorsAdd(businessId, form);
            setForm({ name: "", website: "" });
            load();
            toast.success("Competitor added.");
        } catch { toast.error("Add failed."); }
    };
    const del = async (cid) => {
        try { await api.competitorsDelete(businessId, cid); load(); } catch {}
    };
    const suggest = async () => {
        setBusy("suggest");
        try {
            const r = await api.competitorsSuggest(businessId);
            for (const s of (r.suggested || [])) {
                await api.competitorsAdd(businessId, { name: s.name, reason: s.reason });
            }
            load();
            toast.success(`Added ${r.suggested?.length || 0} suggested competitors.`);
        } catch { toast.error("Suggest failed."); }
        finally { setBusy(null); }
    };
    const analyse = async () => {
        setBusy("analyse");
        try {
            const r = await api.competitorsAnalyse(businessId);
            setAnalysis(r);
        } catch (e) { toast.error(e?.response?.data?.detail || "Analyse failed."); }
        finally { setBusy(null); }
    };
    return (
        <div className="space-y-4" data-testid="competitors-panel">
            <div className="surface rounded-2xl p-4 space-y-3">
                <div className="label-eyebrow">Add competitor</div>
                <div className="flex gap-2">
                    <input data-testid="competitor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Competitor name"
                        className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl p-2 text-sm text-slate-100" />
                    <input data-testid="competitor-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="https://competitor.com (optional)"
                        className="flex-[2] bg-white/[0.03] border border-white/10 rounded-xl p-2 text-sm text-slate-100" />
                    <Button onClick={add} data-testid="competitor-add"
                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 gap-1.5">
                        <Plus className="h-4 w-4" /> Add
                    </Button>
                </div>
                <div className="flex gap-2">
                    <Button onClick={suggest} disabled={busy === "suggest"} variant="outline" data-testid="competitor-suggest"
                        className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5">
                        {busy === "suggest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        AI-suggest competitors
                    </Button>
                    <Button onClick={analyse} disabled={busy === "analyse" || !list.length} data-testid="competitor-analyse"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5">
                        {busy === "analyse" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                        Analyse & compare
                    </Button>
                </div>
            </div>

            <ul className="surface rounded-2xl divide-y divide-white/5" data-testid="competitor-list">
                {list.length === 0 ? (
                    <li className="p-6 text-center text-slate-500 text-sm">No competitors yet.</li>
                ) : list.map((c) => (
                    <li key={c.id} data-testid={`competitor-item-${c.id}`} className="p-3 flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-slate-100">{c.name}</div>
                            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-[11px] text-cyan-300 mono">{c.website}</a>}
                            {c.reason && <div className="text-[11px] text-slate-500 italic">{c.reason}</div>}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => del(c.id)}
                            data-testid={`competitor-delete-${c.id}`}
                            className="border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 h-8">
                            <Trash2 className="h-3 w-3" />
                        </Button>
                    </li>
                ))}
            </ul>

            {analysis && (
                <div className="surface rounded-2xl p-4 space-y-3" data-testid="competitor-analysis">
                    <div className="label-eyebrow">Comparison — source: LIVE_WEBSITE + AI_ANALYSIS</div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-left text-slate-500">
                                <tr><th className="py-2 pr-3">Business</th><th className="pr-3">Pages</th><th className="pr-3">Words</th><th className="pr-3">Schemas</th><th className="pr-3">FAQ</th><th className="pr-3">Wiki</th><th className="pr-3">OSM</th></tr>
                            </thead>
                            <tbody>
                                {[analysis.business, ...(analysis.competitors || [])].map((b, i) => (
                                    <tr key={i} className="border-t border-white/5">
                                        <td className="py-2 pr-3 text-slate-100 font-semibold">{i === 0 ? `${b.name} (You)` : b.name}</td>
                                        <td className="pr-3 mono text-slate-300">{b.pages_crawled ?? "-"}</td>
                                        <td className="pr-3 mono text-slate-300">{b.total_words ?? "-"}</td>
                                        <td className="pr-3 text-slate-300">{(b.schemas_present || []).slice(0, 3).join(", ") || "—"}</td>
                                        <td className="pr-3 text-slate-300">{b.has_faq ? "yes" : "no"}</td>
                                        <td className="pr-3 text-slate-300">{b.external_signals?.wikipedia_present ? "yes" : "—"}</td>
                                        <td className="pr-3 text-slate-300">{b.external_signals?.nominatim_count ?? 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <div className="label-eyebrow text-amber-300 mb-1">What competitors are doing better</div>
                            <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4" data-testid="competitor-doing-better">
                                {(analysis.narrative?.doing_better || []).map((s, i) => <li key={i}>{s}</li>)}
                                {!analysis.narrative?.doing_better?.length && <li className="text-slate-500 list-none">No specific advantages detected.</li>}
                            </ul>
                        </div>
                        <div>
                            <div className="label-eyebrow text-emerald-300 mb-1">Recommendations for us</div>
                            <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4" data-testid="competitor-recommendations">
                                {(analysis.narrative?.recommendations || []).map((s, i) => <li key={i}>{s}</li>)}
                                {!analysis.narrative?.recommendations?.length && <li className="text-slate-500 list-none">No recommendations.</li>}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function KeywordsPanel({ keywords, issues, onFix }) {
    const live = keywords?.live_terms || [];
    const suggested = keywords?.suggested_terms || [];
    return (
        <div className="space-y-4" data-testid="keywords-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="surface rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-cyan-500/20 bg-cyan-500/5 text-cyan-200">source: LIVE_WEBSITE</Badge>
                        <span className="text-sm text-slate-100 font-semibold">Terms currently on the site</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="keywords-live">
                        {live.length === 0 && <span className="text-xs text-slate-500">No terms detected.</span>}
                        {live.map((t) => (
                            <Badge key={t.term} variant="outline" className="border-white/10 bg-white/[0.03] text-slate-300 text-xs">
                                {t.term} <span className="ml-1.5 text-slate-500">· {t.count}</span>
                            </Badge>
                        ))}
                    </div>
                </div>
                <div className="surface rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-indigo-500/20 bg-indigo-500/5 text-indigo-200">source: AI_ANALYSIS</Badge>
                        <span className="text-sm text-slate-100 font-semibold">Suggested keyword opportunities</span>
                    </div>
                    <ul className="space-y-1" data-testid="keywords-suggested">
                        {suggested.length === 0 && <li className="text-xs text-slate-500">No suggestions.</li>}
                        {suggested.map((t, i) => (
                            <li key={i} className="text-xs flex items-center gap-2">
                                <span className={t.covered_on_site ? "text-emerald-200" : "text-amber-200"}>
                                    {t.covered_on_site ? "✓" : "·"}
                                </span>
                                <span className="text-slate-100">{t.term}</span>
                                <span className="text-[10px] text-slate-500 italic">{t.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <IssuesList issues={issues} onFix={onFix} />
        </div>
    );
}

function ScoreCard({ name, data }) {
    const score = data.score;
    const color = score >= 75 ? "emerald" : score >= 45 ? "amber" : "rose";
    const map = {
        emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
        amber: "border-amber-500/30 bg-amber-500/10 text-amber-100",
        rose: "border-rose-500/30 bg-rose-500/10 text-rose-100",
    };
    const labels = {
        website_health: "Website Health",
        technical_seo: "Technical SEO",
        on_page_seo: "On-Page SEO",
        content_seo: "Content SEO",
        local_seo: "Local SEO",
    };
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button data-testid={`seo-score-${name}`}
                    className={`rounded-2xl border p-4 text-left transition-transform hover:scale-[1.02] ${map[color]}`}>
                    <div className="label-eyebrow">{labels[name] || name}</div>
                    <div className="mt-2 text-3xl font-bold">{score}<span className="text-sm text-slate-400">/100</span></div>
                    <div className="mt-2 text-[10px] mono uppercase tracking-widest opacity-60">Click to explain</div>
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-[#0B0F19] border-white/10 text-slate-100">
                <DialogHeader>
                    <DialogTitle>{labels[name] || name} — how this score is computed</DialogTitle>
                    <DialogDescription className="text-xs text-slate-400">
                        Each check is weighted; the score = 100 × (earned / total). Data source per check labelled below.
                    </DialogDescription>
                </DialogHeader>
                <ul className="space-y-2 max-h-[400px] overflow-y-auto pr-2" data-testid={`seo-score-checks-${name}`}>
                    {(data.checks || []).map((c, i) => (
                        <li key={i} className="flex items-start gap-2 py-1.5 border-b border-white/5">
                            {c.outcome === "pass" ? <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
                              : c.outcome === "fail" ? <AlertTriangle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
                              : <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />}
                            <div className="flex-1">
                                <div className="text-sm text-slate-100">{c.label}</div>
                                {c.detail && <div className="text-[11px] text-slate-500 mt-0.5">{c.detail}</div>}
                            </div>
                            <span className="text-[10px] mono text-slate-500">weight {c.weight}</span>
                        </li>
                    ))}
                </ul>
            </DialogContent>
        </Dialog>
    );
}

function PriorityCard({ action, onFix }) {
    const [open, setOpen] = useState(false);
    const sev = SEVERITY_META[action.severity] || SEVERITY_META.medium;
    const cat = CATEGORY_META[action.category];
    return (
        <div className="surface rounded-2xl p-4" data-testid={`priority-action-${action.id}`}>
            <div className="flex items-start gap-4">
                <div className={`h-10 w-10 rounded-xl border flex items-center justify-center shrink-0 ${sev.color}`}>
                    {cat ? <cat.Icon className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-100">{action.title}</span>
                        <Badge variant="outline" className={sev.color}>{sev.label}</Badge>
                        {cat && <Badge variant="outline" className="border-white/10 text-slate-400">{cat.label}</Badge>}
                        {action.difficulty && (
                            <Badge variant="outline" className="border-white/10 text-slate-400">
                                Difficulty: {action.difficulty}
                            </Badge>
                        )}
                        {action.approval_required !== false && (
                            <Badge variant="outline" data-testid={`priority-approval-${action.id}`}
                                   className="border-amber-500/30 bg-amber-500/10 text-amber-200 text-[10px]">
                                Approval required
                            </Badge>
                        )}
                        <Badge variant="outline" className="border-cyan-500/20 bg-cyan-500/5 text-cyan-200 text-[10px]">
                            {action.source || "LIVE_WEBSITE"}
                        </Badge>
                    </div>
                    <button onClick={() => setOpen(!open)}
                        data-testid={`priority-viewpages-${action.id}`}
                        className="mt-1.5 text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1">
                        <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} />
                        View {action.affected_count} {action.affected_count === 1 ? "page" : "pages"}
                    </button>
                    <p className="mt-2 text-sm text-slate-300">{action.reason || action.why_matters}</p>
                    <p className="mt-1 text-xs text-slate-400 italic">{action.recommended_fix}</p>
                    {open && (
                        <ul className="mt-2 space-y-0.5" data-testid={`priority-pagelist-${action.id}`}>
                            {(action.affected_pages || []).slice(0, 20).map((u) => (
                                <li key={u}>
                                    <a href={u} target="_blank" rel="noreferrer"
                                        className="text-[11px] text-cyan-300 hover:text-cyan-200 mono flex items-center gap-1">
                                        <ExternalLink className="h-2.5 w-2.5" /> {u}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {action.can_ai_fix && (
                    <Button size="sm" onClick={() => onFix(action)}
                        data-testid={`priority-fix-${action.id}`}
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5 h-8 shrink-0">
                        <Sparkles className="h-3 w-3" /> Fix with AI
                    </Button>
                )}
            </div>
        </div>
    );
}

function IssuesList({ issues, onFix }) {
    if (!issues.length) {
        return <div className="surface rounded-2xl p-8 text-center text-slate-500 text-sm">No issues in this category.</div>;
    }
    return (
        <div className="space-y-3">
            {issues.map((i) => <IssueRow key={i.id} issue={i} onFix={onFix} />)}
        </div>
    );
}

function IssueRow({ issue, onFix }) {
    const [open, setOpen] = useState(false);
    const sev = SEVERITY_META[issue.severity] || SEVERITY_META.medium;
    return (
        <div className="surface rounded-2xl p-4" data-testid={`issue-${issue.id}`}>
            <div className="flex items-start gap-3">
                <div className={`h-8 w-8 rounded-xl border flex items-center justify-center shrink-0 ${sev.color}`}>
                    <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-100">{issue.title}</span>
                        <Badge variant="outline" className={sev.color}>{sev.label}</Badge>
                        <Badge variant="outline" className="border-cyan-500/20 bg-cyan-500/5 text-cyan-200 text-[10px]">
                            {issue.source}
                        </Badge>
                        <button onClick={() => setOpen(!open)}
                            data-testid={`issue-pages-${issue.id}`}
                            className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1">
                            <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} />
                            {issue.affected_count} {issue.affected_count === 1 ? "page" : "pages"}
                        </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">{issue.why_matters}</p>
                    <p className="mt-1 text-xs text-slate-500 italic">Fix: {issue.recommended_fix}</p>
                    {open && (
                        <ul className="mt-2 space-y-0.5" data-testid={`issue-pagelist-${issue.id}`}>
                            {(issue.affected_pages || []).slice(0, 20).map((u) => (
                                <li key={u}>
                                    <a href={u} target="_blank" rel="noreferrer"
                                        className="text-[11px] text-cyan-300 hover:text-cyan-200 mono flex items-center gap-1">
                                        <ExternalLink className="h-2.5 w-2.5" /> {u}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {issue.can_ai_fix && (
                    <Button size="sm" onClick={() => onFix(issue)}
                        data-testid={`issue-fix-${issue.id}`}
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5 h-8 shrink-0">
                        <Sparkles className="h-3 w-3" /> Fix with AI
                    </Button>
                )}
            </div>
        </div>
    );
}

function PagesTable({ pages }) {
    return (
        <div className="surface rounded-2xl p-4 overflow-x-auto" data-testid="seo-pages-table">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-left text-slate-500">
                        <th className="py-2 pr-3">URL</th>
                        <th className="pr-3">Status</th>
                        <th className="pr-3">Title</th>
                        <th className="pr-3">Meta desc</th>
                        <th className="pr-3">Words</th>
                        <th className="pr-3">H1</th>
                        <th className="pr-3">Schema</th>
                    </tr>
                </thead>
                <tbody>
                    {pages.map((p) => (
                        <tr key={p.url} className="border-t border-white/5"
                            data-testid={`page-row-${(p.url || '').replace(/[^a-z0-9]/gi, '').slice(0, 30)}`}>
                            <td className="py-2 pr-3 text-cyan-300 mono max-w-[280px] truncate">
                                <a href={p.url} target="_blank" rel="noreferrer">{p.url}</a>
                            </td>
                            <td className="pr-3 mono text-slate-400">{p.status || "-"}</td>
                            <td className="pr-3 text-slate-300 max-w-[220px] truncate">{p.title || <span className="text-rose-300">missing</span>}</td>
                            <td className="pr-3 text-slate-300 max-w-[240px] truncate">{p.meta_description || <span className="text-rose-300">missing</span>}</td>
                            <td className="pr-3 mono text-slate-400">{p.word_count ?? "-"}</td>
                            <td className="pr-3 mono text-slate-400">{p.h1_count ?? "-"}</td>
                            <td className="pr-3 text-slate-400">{p.has_schema ? "yes" : "no"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PSIPanel({ psi }) {
    if (!psi || !psi.available) {
        return (
            <div className="surface rounded-2xl p-6 text-sm text-slate-300" data-testid="seo-psi-unavailable">
                <div className="flex items-center gap-2 text-amber-200 mb-2">
                    <Info className="h-4 w-4" /> PageSpeed data unavailable
                </div>
                <p className="text-slate-400 text-xs">{psi?.error || "PSI API did not return data. This is recorded honestly — never estimated."}</p>
            </div>
        );
    }
    return (
        <div className="surface rounded-2xl p-5 space-y-4" data-testid="seo-psi-available">
            <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="border-cyan-500/20 bg-cyan-500/5 text-cyan-200">
                    Source: {psi.source} · {psi.strategy}
                </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PSIMetric label="Performance" value={psi.performance_score} suffix="/100" />
                <PSIMetric label="SEO score" value={psi.seo_score} suffix="/100" />
                <PSIMetric label="LCP" value={psi.lcp_display} />
                <PSIMetric label="CLS" value={psi.cls_display} />
                <PSIMetric label="TBT" value={psi.tbt_display} />
                <PSIMetric label="FCP" value={psi.fcp_display} />
                <PSIMetric label="Speed Index" value={psi.speed_index_display} />
            </div>
            {psi.opportunities?.length > 0 && (
                <div>
                    <div className="label-eyebrow mb-2">Top opportunities</div>
                    <ul className="space-y-1">
                        {psi.opportunities.map((o) => (
                            <li key={o.id} className="text-xs text-slate-300">
                                <span className="text-slate-100">{o.title}</span>
                                {o.display && <span className="text-slate-500"> · {o.display}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function PSIMetric({ label, value, suffix = "" }) {
    return (
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
            <div className="label-eyebrow">{label}</div>
            <div className="mt-1 text-lg font-bold text-slate-100">{value ?? "—"}{value != null && suffix}</div>
        </div>
    );
}

function HistoryList({ history }) {
    if (!history.length) return <div className="surface rounded-2xl p-8 text-center text-slate-500 text-sm">No scans yet.</div>;
    return (
        <ul className="surface rounded-2xl divide-y divide-white/5" data-testid="seo-history">
            {history.map((h) => (
                <li key={h.id} className="p-4 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm text-slate-100">{h.kind === "quick" ? "Quick" : "Full"} scan · {h.status}</div>
                        <div className="text-[11px] text-slate-500 mono">{h.started_at?.slice(0, 19).replace('T', ' ')} · {h.root_url}</div>
                    </div>
                    <div className="text-xs text-slate-400">
                        {h.scores?.overall_seo?.score != null && <span>SEO: <span className="text-slate-100 font-bold">{h.scores.overall_seo.score}</span></span>}
                    </div>
                </li>
            ))}
        </ul>
    );
}

